import type { MonthlyUsage } from "../contexts/UsageContext";
import { RELATIVE_TIME_FORMAT } from "./ui";

// `month` keys are UTC ('YYYY-MM-01'); pin the formatter to UTC too, or the
// label shifts to the adjacent month for anyone off UTC — e.g.
// '2026-09-01T00:00:00Z' is still Aug 31 evening west of UTC.
export function monthLabel(month: string): string {
    return new Intl.DateTimeFormat(undefined, {
        month: "short",
        timeZone: "UTC",
    }).format(new Date(`${month}T00:00:00Z`));
}

/**
 * The server only returns months the user actually made calls in, so a quiet
 * month has no row at all. Lay those back onto a continuous run of months so the
 * axis reads as a timeline rather than a list of whichever months happened to
 * have traffic.
 */
export function fillMonths(history: MonthlyUsage[], months: number, now: Date = new Date()): MonthlyUsage[] {
    const byMonth = new Map(
        history.map((row) => [
            row.month,
            row.calls,
        ]),
    );
    const filled: MonthlyUsage[] = [];

    for (let offset = months - 1; offset >= 0; offset--) {
        const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - offset, 1));
        const key = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-01`;
        filled.push({
            month: key,
            calls: byMonth.get(key) ?? 0,
        });
    }

    return filled;
}

/**
 * The usage reset moment in the viewer's own calendar, e.g. "1 Nov" or "Nov 1". The server sends
 * the start of next month in UTC, which can still be the 31st west of UTC, so this deliberately
 * uses local time: it's the day the viewer will actually see calls come back.
 */
export function formatResetDate(resetsAt: string): string {
    return new Intl.DateTimeFormat(undefined, {
        day: "numeric",
        month: "short",
    }).format(new Date(resetsAt));
}

/** A subscription period end in the viewer's locale, e.g. "Nov 12, 2026". */
export function formatPeriodEnd(periodEnd: string): string {
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
    }).format(new Date(periodEnd));
}

/**
 * How far away the reset is, counted in the viewer's calendar days: "today", "tomorrow" or
 * "in 19 days". Calendar days rather than 24 hour blocks, so it agrees with formatResetDate.
 */
export function formatResetCountdown(resetsAt: string, now: Date = new Date()): string {
    const reset = new Date(resetsAt);
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfResetDay = new Date(reset.getFullYear(), reset.getMonth(), reset.getDate());
    const days = Math.round((startOfResetDay.getTime() - startOfToday.getTime()) / (24 * 60 * 60 * 1000));
    // A reset already past reads as "today" until the new period's usage arrives.
    return RELATIVE_TIME_FORMAT.format(Math.max(0, days), "day");
}
